import ROUTES from 'constants/routes';
import { USER_ROLES } from 'constants/auth';

import {
  Login,
  UsersList,
  ResetPassword,
  ConfirmPassword,
  EditUser,
  Hours,
  RestaurantsList,
  RestaurantProfile,
  TermsAndConditions,
  Orders,
  ChangePassword,
  Item,
  EditModifier,
  EditSchedules,
  Menu,
  EditLabels,
  Boundaries,
  PromotionsList,
  EditPromotion,
  Settings,
  Reviews,
  Reports,
  Messages,
  PublicTerms,
} from 'components/';
import AccessDenied from 'pages/noAccessPages/AccessDenied';
import NotFound from 'pages/noAccessPages/NotFound';

export const DEFAULT_ROUTES = {
  [USER_ROLES.ROOT]: ROUTES.RESTAURANTS,
  [USER_ROLES.MANAGER]: ROUTES.ORDERS,
};

export const getDefaultRoute = user => {
  if (!user || !user.role) return ROUTES.LOGIN;
  if (user.role === USER_ROLES.ADMIN) return `${ROUTES.RESTAURANTS}/${user.restaurant.id}/edit`;
  return DEFAULT_ROUTES[user.role];
};

const routes = [
  {
    path: ROUTES.LOGIN,
    exact: true,
    name: 'Login',
    component: Login,
    isProtected: false,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    exact: true,
    name: 'Reset Password',
    component: ResetPassword,
    isProtected: false,
  },
  {
    path: ROUTES.CONFIRM_PASSWORD,
    exact: true,
    name: 'Set New Password',
    component: ConfirmPassword,
    isProtected: false,
  },
  {
    path: ROUTES.RESTAURANTS,
    exact: true,
    name: 'Restaurants List',
    component: RestaurantsList,
    permissions: [USER_ROLES.ROOT],
  },
  {
    path: ROUTES.RESTAURANTS_DASHBOARD,
    exact: true,
    name: 'Restaurants Dashboard',
    component: Login,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.ORDERS,
    exact: false,
    name: 'Manage orders',
    component: Orders,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    path: `${ROUTES.MESSAGES}/:id?`,
    exact: false,
    name: 'Messages',
    component: Messages,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    path: ROUTES.USERS_LIST,
    exact: true,
    name: 'Users List',
    component: UsersList,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.USER}/:id/edit`,
    exact: false,
    name: 'Edit User',
    component: EditUser,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.USER}/add`,
    exact: true,
    name: 'Add User',
    component: EditUser,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.EDIT_PROFILE,
    exact: true,
    name: 'Edit Profile',
    component: EditUser,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    path: `${ROUTES.MODIFIERS}/add`,
    exact: true,
    name: 'Add modifier',
    component: EditModifier,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.MODIFIERS}/:id/edit`,
    exact: true,
    name: 'Edit modifier',
    component: EditModifier,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.CHANGE_PASSWORD,
    exact: true,
    name: 'Change Password',
    component: ChangePassword,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    path: `${ROUTES.HOURS}`,
    exact: true,
    name: 'Hours',
    component: Hours,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.RESTAURANTS}/:id/edit`,
    exact: false,
    name: 'Edit Restaurant',
    component: RestaurantProfile,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.RESTAURANTS}/add`,
    exact: true,
    name: 'Add Restaurant',
    component: RestaurantProfile,
    permissions: [USER_ROLES.ROOT],
  },
  {
    path: ROUTES.EDIT_TERMS_AND_CONDITIONS,
    exact: true,
    name: 'Edit Terms and Conditions',
    component: TermsAndConditions,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.ACCESS_DENIED,
    exact: true,
    name: 'Access Denied',
    component: AccessDenied,
    isProtected: false,
  },
  {
    path: ROUTES.NOT_FOUND,
    exact: true,
    name: 'Not Found',
    component: NotFound,
  },
  {
    path: '/section/:sectionId/item/:itemId/edit',
    exact: true,
    name: 'Item',
    component: Item,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.ITEM}/add`,
    exact: true,
    name: 'Item',
    component: Item,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.MENU_SCHEDULE}/:id/edit`,
    exact: false,
    name: 'Edit Schedules',
    component: EditSchedules,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.MENU_SCHEDULE}/add`,
    exact: false,
    name: 'Add Schedules',
    component: EditSchedules,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.MENU_LABELS}/:id/edit`,
    exact: false,
    name: 'Edit Labels',
    component: EditLabels,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.MENU_LABELS}/add`,
    exact: false,
    name: 'Add Labels',
    component: EditLabels,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.MENU}`,
    exact: false,
    name: 'Menu',
    component: Menu,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.SETTINGS,
    exact: true,
    name: 'Settings',
    component: Settings,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    path: `${ROUTES.BOUNDARIES}`,
    name: 'Boundaries',
    component: Boundaries,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.PROMOTIONS}`,
    exact: true,
    name: 'Promotions',
    component: PromotionsList,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.PROMOTIONS}/add`,
    exact: true,
    name: 'Add Promotion',
    component: EditPromotion,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.PROMOTIONS}/:id/edit`,
    exact: true,
    name: 'Edit Promotion',
    component: EditPromotion,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.REVIEWS,
    exact: true,
    name: 'Reviews',
    component: Reviews,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: `${ROUTES.REPORTS}`,
    exact: false,
    name: 'Reports',
    component: Reports,
    permissions: [USER_ROLES.ROOT, USER_ROLES.ADMIN],
  },
  {
    path: ROUTES.TERMS_AND_CONDITIONS,
    exact: true,
    name: 'Terms and Conditions',
    component: PublicTerms,
    isProtected: false,
  },
  {
    path: '/',
    exact: true,
    name: 'main',
    component: null,
  },
];

export default routes;

